import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import Plate from '../components/Plate';
import { RefreshCw } from 'lucide-react';
import DishSheet from '../components/DishSheet';
import BillSheet from '../components/BillSheet';
import { DetailSheet as IngredientSheet } from './Ingredients';
import axiosInstance from '../utils/axiosInstance';

const eur = (v) => `${Number(v || 0).toFixed(2).replace('.', ',')}\u00a0€`;

const Section = ({ title, hint, children }) => (
    <section className="menu-section">
        <h2>{title}</h2>
        {hint && <p>{hint}</p>}
        <div className="mt-2">{children}</div>
    </section>
);

const Row = ({ name, value, onClick }) => (
    <button type="button" className="menu-row" onClick={onClick}>
        <span className="menu-row-name">{name}</span>
        <span className="price whitespace-nowrap">{value}</span>
    </button>
);

const Empty = ({ children }) => <p className="py-2 text-sm text-muted-foreground">{children}</p>;

const billName = (sync) => {
    const n = (sync.invoice_number || '').trim();
    return !n || n === '/' ? 'brouillon' : n;
};

/** Tableau de bord: what needs doing first, then the headline numbers. */
const Dashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    // The row opened in a sheet, right here on the dashboard.
    const [openDish, setOpenDish] = useState(null);
    const [openIngredient, setOpenIngredient] = useState(null);
    const [openBill, setOpenBill] = useState(null);

    const fetchDashboard = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('dashboard/');
            setData(response.data);
        } catch (error) {
            console.error('Failed to fetch dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'Tableau de bord | Costy';
        fetchDashboard();
    }, []);

    const showDish = async (id) => {
        try {
            const [dish, settings] = await Promise.all([
                axiosInstance.get(`/dishes/${id}/`),
                axiosInstance.get('/costing-settings/'),
            ]);
            setOpenDish({ dish: dish.data, target: parseFloat(settings.data.target_food_cost) });
        } catch (error) {
            console.error('Failed to fetch dish:', error);
            alert('Impossible d’ouvrir ce plat. Réessayez.');
        }
    };

    const showIngredient = async (id) => {
        try {
            const { data: ingredient } = await axiosInstance.get(`/ingredients/${id}/`);
            setOpenIngredient(ingredient);
        } catch (error) {
            console.error('Failed to fetch ingredient:', error);
            alert('Impossible d’ouvrir cet ingrédient. Réessayez.');
        }
    };

    const applyDishPrice = async (price) => {
        try {
            await axiosInstance.patch(`/dishes/${openDish.dish.id}/`, { selling_price: price });
            setOpenDish(null);
            fetchDashboard();
        } catch (error) {
            console.error('Failed to set selling price:', error);
            alert('Enregistrement impossible.');
        }
    };

    const deleteDish = async () => {
        if (!confirm('Supprimer ce plat ?')) return;
        try {
            await axiosInstance.delete(`/dishes/${openDish.dish.id}/`);
            setOpenDish(null);
            fetchDashboard();
        } catch (error) {
            console.error('Failed to delete dish:', error);
            alert('Suppression impossible.');
        }
    };

    const needsPrice = data?.needs_price ?? [];
    const lowMargin = data?.low_margin ?? [];
    const priceChanges = data?.price_changes ?? [];
    const unpriced = data?.unpriced_ingredients ?? [];

    // The average margin, said the way a kitchen thinks about it.
    const margin = Number(data?.avg_profit_margin || 0);
    const hasFigure = data?.costed_dishes > 0;
    const per10 = (100 - margin) / 10;
    const drinkPer10 = (100 - Number(data?.avg_drink_margin || 0)) / 10;

    return (
        <Layout>
            <div className="dash space-y-8">
                <PageHeader
                    title="Tableau de bord"
                    below={hasFigure ? (
                        <div className="mt-4 flex items-center gap-4">
                            <Plate share={(100 - margin) / 100} size={64} />
                            <div className="min-w-0">
                                <p className="font-display dash-headline">
                                    Sur 10 € de plats vendus, {eur(per10)} partent en ingrédients.
                                </p>
                                <p className="page-sub">
                                    Moyenne hors TVA sur {data.costed_dishes} plats chiffrés, sans les boissons. Coût moyen d’une portion : {eur(data.avg_portion_cost)}. {data.no_recipe_count} plats sur {data.total_dishes} sans recette.
                                    {data.costed_drinks > 0 && ` Boissons : ${eur(drinkPer10)} d’achat sur 10 € vendus (${data.costed_drinks} chiffrées).`}
                                </p>
                            </div>
                        </div>
                    ) : null}
                >
                    <button type="button" onClick={fetchDashboard} aria-label="Actualiser" className="btn-outline !px-3 sm:!px-[1.125rem]">
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">Actualiser</span>
                    </button>
                </PageHeader>

                {!data ? (
                    <p className="text-sm text-muted-foreground">{loading ? 'Chargement…' : 'Impossible de charger le tableau de bord. Actualisez la page.'}</p>
                ) : (
                    <div className="menu-columns">
                        <Section title="Marges à revoir" hint="Moins de 60 %, la plus basse d’abord.">
                            {lowMargin.length === 0
                                ? <Empty>Rien sous 60 %.</Empty>
                                : lowMargin.map((d) => (
                                    <Row key={d.id} name={d.name} value={`${d.margin.toFixed(1).replace('.', ',')} %`} onClick={() => showDish(d.id)} />
                                ))}
                        </Section>

                        {data.low_margin_drinks?.length > 0 && (
                            <Section title="Boissons à revoir" hint="Moins de 60 %, la plus basse d’abord.">
                                {data.low_margin_drinks.map((d) => (
                                    <Row key={d.id} name={d.name} value={`${d.margin.toFixed(1).replace('.', ',')} %`} onClick={() => showDish(d.id)} />
                                ))}
                            </Section>
                        )}

                        <Section title="Prix de vente à définir" hint="La recette est là, il manque le prix.">
                            {needsPrice.length === 0
                                ? <Empty>Tous les plats chiffrés ont un prix.</Empty>
                                : needsPrice.map((d) => (
                                    <Row key={d.id} name={d.name} value={`coût ${eur(d.cost)}`} onClick={() => showDish(d.id)} />
                                ))}
                        </Section>

                        <Section title="Questions pour la cuisine" hint={`${data.open_questions_count} à traiter.`}>
                            {data.open_questions.length === 0
                                ? <Empty>Aucune question ouverte.</Empty>
                                : data.open_questions.map((q) => (
                                    <button key={q.id} type="button" onClick={() => navigate('/questions')} className="menu-row">
                                        <span className="menu-question" title={q.text}>{q.dish && <b>{q.dish} : </b>}{q.text}</span>
                                    </button>
                                ))}
                        </Section>

                        <Section
                            title="Prix d’achat à vérifier"
                            hint={`${data.price_changes_count} changés sur facture, ${data.stale_prices_count} confirmés il y a plus de 4 mois.`}
                        >
                            {priceChanges.length === 0
                                ? <Empty>Aucune facture n’a changé de prix.</Empty>
                                : priceChanges.map((i) => (
                                    <Row key={i.id} name={i.name} value={`${eur(i.price)}/${i.unit}`} onClick={() => showIngredient(i.id)} />
                                ))}
                            {unpriced.length > 0 && (
                                <p className="mt-3 menu-note text-muted-foreground">
                                    <span className="font-semibold text-foreground">Sans prix :</span> {unpriced.join(', ')}
                                </p>
                            )}
                        </Section>

                        <Section title="Dernières factures">
                            {data.recent_syncs.length === 0
                                ? <Empty>Aucune facture. Importez-les depuis Fournisseurs.</Empty>
                                : data.recent_syncs.map((s) => (
                                    <Row
                                        key={s.id}
                                        name={`${s.supplier_name}, ${billName(s)}`}
                                        value={new Date(s.invoice_date).toLocaleDateString('fr-BE')}
                                        onClick={() => setOpenBill(s)}
                                    />
                                ))}
                        </Section>
                    </div>
                )}
            </div>

            {openDish && (
                <DishSheet
                    dish={openDish.dish}
                    targetFoodCost={openDish.target}
                    onClose={() => setOpenDish(null)}
                    onDelete={deleteDish}
                    onEdit={() => navigate(`/dishes?edit=${openDish.dish.id}`)}
                    onUsePrice={applyDishPrice}
                />
            )}
            {openIngredient && (
                <div className="gm">
                    <IngredientSheet
                        ingredient={openIngredient}
                        onClose={() => setOpenIngredient(null)}
                        onChanged={() => { setOpenIngredient(null); fetchDashboard(); }}
                    />
                </div>
            )}
            {openBill && <BillSheet bill={openBill} onClose={() => setOpenBill(null)} />}
        </Layout>
    );
};

export default Dashboard;
